import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import * as Constants from "Data/Constants";
import * as TestJSON from "json/testSong.json";
import { SongData } from "Models/SongData";
import * as Topics from "Data/Topics";
import {MusicGame} from "States/MusicGame";
import { Button } from "GameObjects/Button";
import { ISoloRequestData } from "Interfaces/ISoloRequestData";
import { MusicGameCharacter } from "GameObjects/MusicGame/Character";
import * as Base64Utils from "Utils/base64";
import { TweenLite } from "gsap";

const MUSIC_LAYER_TOP: string = 'backing_loop_1';
const MUSIC_LAYER_BASE: string = 'backing_loop_0';
const COUNTDOWN_DELAY = 3;

export class MusicManager {
    /** Maximum difference in seconds for how out of sync the soloist and the backing track are allowed to be */
    protected static readonly MAX_PLAY_TIME_DIFF: number = 0.0016;
    /**
     * Minimum duration, in seconds, between solo request time and assigned start time.
     *
     * i.e. if there's not enough time to prepare for your solo, wait until the following start point
     */
    protected static readonly MIN_NEXT_TIME_DIFF: number = 0.5;

    private static _Instance: MusicManager;
    protected _data: any = {};
    // protected _onboarding: {complete: boolean} = {complete: false};
    protected _baseTrack: HLF.IAudioTrack;
    protected _topTrack: HLF.IAudioTrack;
    protected _nextTimeStamp: number = 0;
    protected _bgSongData: SongData;
    protected _lastAvailableTimeStampValue: number;
    protected _barTime: number;
    protected _subscriptions: any[];
    protected _previousSongTime: number = 0;
    protected _newSongTime: number = 0;
    protected _nextTimestampSolos: any[];
    protected _currentTimestampSolos: any[];
    protected _recording: boolean = false;
    protected _preRecording: boolean = false;
    protected _playback: boolean = false;

    constructor() {
        this._bgSongData = new SongData(TestJSON.bg.timestamps);
        this._lastAvailableTimeStampValue = this._bgSongData.TimeStamps[this._bgSongData.TimeStamps.length - 2];
        this._barTime = this._bgSongData.TimeStamps[0];
        this._nextTimestampSolos = new Array<{}>();
        this._currentTimestampSolos = new Array<{}>();

        this._topTrack =
            Application.instance.audio.getAudioTrack(MUSIC_LAYER_TOP, HLF.AudioCategory.BGM) ??
            Application.instance.audio.createAudioTrack (MUSIC_LAYER_TOP, HLF.AudioCategory.BGM);
        this._baseTrack =
            Application.instance.audio.getAudioTrack (MUSIC_LAYER_BASE, HLF.AudioCategory.BGM) ??
            Application.instance.audio.createAudioTrack (MUSIC_LAYER_BASE, HLF.AudioCategory.BGM);
        this._baseTrack.on(HLF.HowlerUtils.Events.END, this.onBaseLoop.bind(this));

        this._subscriptions = new Array<any>();
        this._subscriptions.push(
            PubSub.subscribe(Topics.REQUEST_SOLO, this.RequestSolo.bind(this)),
            PubSub.subscribe(Topics.REQUEST_CANCEL_SOLO, this.CancelSolo.bind(this)),
            PubSub.subscribe(Topics.REQUEST_START_RECORDING, this.StartRecording.bind(this)),

            PubSub.subscribe(Topics.SOLO_STARTED, this.RecordEvent.bind(this)),
            PubSub.subscribe(Topics.SOLO_STOPPED, this.RecordEvent.bind(this)),

            PubSub.subscribe(HLF.GAME_PAUSED, this.onPause.bind(this)),
            PubSub.subscribe(HLF.GAME_UNPAUSED, this.onResume.bind(this)),

        );
    }

    public static get Instance(): MusicManager {
        if (MusicManager._Instance === undefined) {
            MusicManager._Instance = new MusicManager();
            MusicManager._Instance.Load();
            // MusicManager._Instance.Reset();
        }
        return MusicManager._Instance;
    }
    public get recording() {
        return this._recording;
    }
    public get data() {
        return this._data;
    }

    // public get onboarding() {
    //     return this._onboarding;
    // }

    // /** Current time of the backing track */
    // public get DEBUG_currentTime(): string {
    //     return this._baseTrackTEMP!.getTimePos().toFixed(4).toString();
    // }
    // /** The final solo start time, before the track loops */
    // public get DEBUG_lastAvailableTime(): string {
    //     return this._lastAvailableTimeStampValue.toString();
    // }
    // /** The next upcoming solo start time */
    // public get DEBUG_nextAvailableTime(): string {
    //     return this._bgSongData.TimeStamps[this._nextTimeStamp].toString();
    // }

    public destroy(): void {
        this._topTrack.stop();
        this._baseTrack.stop();
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    public update(pDeltaTime: number) {
        if (this._baseTrack !== undefined) {
            this._previousSongTime = this._newSongTime;
            this._newSongTime = this._baseTrack.getTimePos();
            const curTimeStamp = this._bgSongData.TimeStamps[this._nextTimeStamp];
            const firstTimeStamp = this._bgSongData.TimeStamps[0];

            // is it time to go to the next bar?
            if((this._newSongTime >= curTimeStamp)) {
                // and this is not the final bar?
                if (this._nextTimeStamp < this._bgSongData.TimeStamps.length - 1) {
                    this._nextTimeStamp = (++this._nextTimeStamp) % this._bgSongData.TimeStamps.length;
                    PubSub.publishSync(Topics.PLAYHEAD_MOVED, curTimeStamp);

                    // trigger any queued solos
                    this.shiftCurSoloists();
                }
            }
            // go to first bar
            else if (this._previousSongTime > this._newSongTime) {
                this._nextTimeStamp = 0;
                PubSub.publishSync(Topics.PLAYHEAD_MOVED, 0);
                this.shiftCurSoloists();
            }

            if (this._playback) {
                if(this.data && this.data.events && this.data.events.length) {
                    for (let i=0; i<this.data.events.length; i++) {
                        if(this.data.events[i].time <= this._newSongTime) {
                            if (!this.data.events[i].consumed) {
                                this.ConsumeRecordedEvent(this.data.events[i]);
                            }
                        }
                        // assume events are sorted by time so we can break out of the loop early
                        else {
                            break;
                        }
                    }
                }
            }

        }

    }

    public Play(pWithRecordedEvents: boolean = false) {
        this._playback = pWithRecordedEvents;
        if (!this._playback) {
            this._data.events = [];
            PubSub.publishSync(Topics.RECORDING_UPDATED, this.data);
        }
        else {
            // console.log(this.data.events);
        }
        Application.instance.audio.play(MUSIC_LAYER_TOP, 1, true, HLF.AudioCategory.BGM);
        Application.instance.audio.play(MUSIC_LAYER_BASE, 1, true, HLF.AudioCategory.BGM);
        PubSub.publishSync(Topics.PLAYBACK_STARTED, true);
        PubSub.publishSync(Topics.PLAYHEAD_MOVED, 0);
        // trigger any queued solos
        this.shiftCurSoloists();
    }

    public Reset() {
        this._data = {events: []};
        // this._onboarding = {complete: false};
        // location.hash = "#";
        this.Save();
    }

    public Export(): string {
        return Base64Utils.toBase64(JSON.stringify(this._data));
    }

    protected ConsumeRecordedEvent(pEvent: any) {
        pEvent.consumed = true;
        switch(pEvent.event) {
            case Topics.SOLO_STARTED:
                PubSub.publishSync(Topics.START_SOLO, pEvent);
                break;
            case Topics.SOLO_STOPPED:
                if(pEvent.time !== 0) {
                    PubSub.publishSync(Topics.STOP_SOLO, pEvent);
                }
                break;
            default:
                console.error("Unknown event ", pEvent);
                break;
        }
    }

    protected Save() {
        // Application.instance.saveManager.storeString("onboarding", Base64Utils.toBase64(JSON.stringify(this._onboarding)), 1);
        // location.hash = Base64Utils.toBase64(JSON.stringify(this._data));
    }

    protected Load() {
        try {
            if(location.hash && location.hash.length) {
                this._data = JSON.parse(Base64Utils.fromBase64(location.hash.substr(1)));
            }
            else {
                console.log("no saved state to load");
                this.Reset();
            }
            // console.log(Base64Utils.fromBase64(location.hash.substr(1)));
            // console.log(this._data);
            // this._onboarding = JSON.parse(
            //     Base64Utils.fromBase64(Application.instance.saveManager.readString("onboarding", undefined, 1)));
        }
        catch (e) {
            console.error("Error loading saved state. Resetting.", e);
            this.Reset();
        }
    }

    // TODO: unit tests?
    /**
     * If there is time for the next solo, add to currentTimestamps
     * If there isn't, add to nextTimestamps
     */
    protected RequestSolo(pTopic: string, pData: ISoloRequestData): void {
        // Determine when to play
        const nextAvailableTime: number = this._bgSongData.TimeStamps[this._nextTimeStamp];
        const trackTimePos: number = this._baseTrack.getTimePos();
        const timeToNextTimeStamp: number = nextAvailableTime - trackTimePos;
        let waitTime;
        let progressBarMinimum;
        let progressBarMaximum;
        let bar;
        let time;

        // After last time stamp value or before the start
        if (trackTimePos > this._lastAvailableTimeStampValue || trackTimePos === 0) {
            bar = 0;
            time = 0;

            // queued for bar zero, and playback is currently paused
            if (trackTimePos === 0) { // TODO: confirm this can't happen in any other situation
                waitTime = COUNTDOWN_DELAY;
            }
            // Less than min delay time
            else if (timeToNextTimeStamp < MusicManager.MIN_NEXT_TIME_DIFF) {
                waitTime = timeToNextTimeStamp + this._barTime;
            }
            // Greater than min delay time
            else {
                waitTime = timeToNextTimeStamp;
            }

            progressBarMinimum = 0;
            progressBarMaximum = waitTime;
        }
        // Before last time stamp value
        else {
            progressBarMinimum = trackTimePos;
            bar = this._nextTimeStamp + 1;
            time = nextAvailableTime;

            // Less than min delay time
            if (timeToNextTimeStamp < MusicManager.MIN_NEXT_TIME_DIFF) {
                waitTime = timeToNextTimeStamp + this._barTime;
                progressBarMaximum = trackTimePos + waitTime;
            }
            // Greater than min delay time
            else {
                waitTime = timeToNextTimeStamp;
                progressBarMaximum = nextAvailableTime;
            }
        }

        this.addSoloToQueue({
            character: pData.character,
            loop: pData.loop,
            bar,
            time,
        });

        PubSub.publishSync(Topics.SOLO_QUEUED, {
            character: pData.character,
            loop: pData.loop,
            bar,
            time,
            progressBarMinimum,
            progressBarMaximum,
        });
        // console.log("WaitTime: %c%s", "color: blue", waitTime);
    }
    protected StartSolo(pData: {character: number, loop: number, bar: number, time: number}): void {

        Application.instance.audio.fadeTo(MUSIC_LAYER_TOP, HLF.AudioCategory.BGM, 0, 100); // TODO
        PubSub.publishSync(Topics.START_SOLO, {
            character: pData.character,
            loop: pData.loop,
            bar: pData.bar,
            time: this._baseTrack.getTimePos(),
        });
    }

    protected addSoloToQueue(pData: {
        character: number,
        loop: number,
        bar: number,
        time: number,
    }) {
        for (let i = this._currentTimestampSolos.length - 1; i >= 0; --i) {
            if(
                this._currentTimestampSolos[i].character === pData.character &&
                this._currentTimestampSolos[i].bar === pData.bar
            ) {
                const cancelled = this._currentTimestampSolos.splice(i, 1);
                console.log("replace queued solo", cancelled);
            }
        }
        this._currentTimestampSolos.push(pData);
        // console.log("queued solo for bar " + (pData.bar), pData);
    }

    protected shiftCurSoloists(): void {
        for (let i = this._currentTimestampSolos.length - 1; i >= 0; --i) {
            const soloist = this._currentTimestampSolos.pop()!;
            this.StartSolo(soloist);
        }
        for (let i = this._nextTimestampSolos.length - 1; i >= 0; --i) {
            const soloist = this._nextTimestampSolos.pop()!;
            this._currentTimestampSolos.push(soloist);
        }
    }

    protected CancelSolo(pTopic: string, pData: ISoloRequestData): void {
        console.log("request cancel", pData);
        for (let i = this._currentTimestampSolos.length - 1; i >= 0; --i) {
            if (this._currentTimestampSolos[i].character === pData.character) {
                const cancelled = this._currentTimestampSolos.splice(i, 1);
                console.log("cancelled queued solo", cancelled);
            }
        }
        for (let i = this._nextTimestampSolos.length - 1; i >= 0; --i) {
            if (this._nextTimestampSolos[i].character === pData.character) {
                const cancelled = this._nextTimestampSolos.splice(i, 1);
                console.log("cancelled queued solo", cancelled, this._nextTimestampSolos[i]);
            }
        }
        PubSub.publishSync(Topics.STOP_SOLO, pData);
    }

    protected CancelAllSolos() {
        this._currentTimestampSolos = [];
        this._nextTimestampSolos = [];
        PubSub.publishSync(Topics.STOP_ALL_SOLOS, {});
    }

    protected StartRecording(pTopic: string, pData: any) {
        if (this.recording || this._preRecording) return;
        this.CancelAllSolos();
        if(!this._baseTrack.isPlaying()) {
            this.Play();
        }
        this._data.events = [];
        this._newSongTime = 0;
        this._nextTimeStamp = 0;
        this._topTrack.setTimePos(this._baseTrack.getDuration() - COUNTDOWN_DELAY);
        this._baseTrack.setTimePos(this._baseTrack.getDuration() - COUNTDOWN_DELAY);
        this.Save();

        this._preRecording = true;
        PubSub.publishSync(Topics.RECORDING_COUNT_IN, COUNTDOWN_DELAY);
        PubSub.publishSync(Topics.PLAYHEAD_MOVED, this._baseTrack.getDuration() - COUNTDOWN_DELAY);
        PubSub.publishSync(Topics.RECORDING_UPDATED, this.data);

    }

    protected Stop() {
        this._baseTrack.stop();
        this._topTrack.stop();
        // this._topTrack.setTimePos(0);
        // this._baseTrack.setTimePos(0);
        // this._newSongTime = 0;
        // this._nextTimeStamp = 0;
        this.CancelAllSolos();
        PubSub.publishSync(Topics.PLAYBACK_ENDED, false);
    }

    protected StopRecording() {
        this._recording = false;
        this.Stop();
        PubSub.publishSync(Topics.RECORDING_ENDED, {});
        this.Save();
    }

    protected RecordEvent(pTopic: string, pData: any) {
        if (this._playback) return;
        if (this.data.events === undefined) {
            this._data.events = [];
        }
        let time = this._baseTrack.getTimePos();
        if(pTopic === Topics.SOLO_STOPPED && time === 0) {
            time = this._baseTrack.getDuration();
        }
        this._data.events.push({
            event: pTopic,
            time,
            ...pData,
        });
        // console.log(this.data.events);
        this.Save();
        PubSub.publishSync(Topics.RECORDING_UPDATED, this.data);
    }

    protected onBaseLoop(): void {
        console.log("OnBaseLoop");
        if (this._recording) {
            this.StopRecording();
        }
        else if (this._preRecording) {
            this._preRecording = false;
            this._recording = true;
            PubSub.publishSync(Topics.RECORDING_STARTED, this.data);
        }
        else if (this._playback) {
            if(this.data && this.data.events && this.data.events.length) {
                for (let i=0; i<this.data.events.length; i++) {
                    this.data.events[i].consumed = false;
                }
            }
        }
        else {
            const newEvents: any = {};
            // grab only the final event for each character
            if(this.data && this.data.events && this.data.events.length) {
                for (let i=0; i<this.data.events.length; i++) {
                    const event = this.data.events[i];
                    if (!newEvents[event.character] || event.time > newEvents[event.character].time) {
                        if(event.event === Topics.SOLO_STARTED) {
                            newEvents[this.data.events[i].character] = this.data.events[i];
                        }
                        else {
                            newEvents[this.data.events[i].character] = undefined;
                            delete newEvents[this.data.events[i].character];
                        }
                    }
                }
            }
            this._data.events = Object.values(newEvents).map((event: any, i, a)=>{event.time = 0; return event;});
            // console.log(this._data.events);
            PubSub.publishSync(Topics.RECORDING_UPDATED, this.data);
        }
        // sync tracks
        this._topTrack.setTimePos(0);
        this._baseTrack.setTimePos(0);
        this._newSongTime = 0;
        this._nextTimeStamp = 0;
        PubSub.publishSync(Topics.PLAYHEAD_MOVED, 0);
        // trigger any queued solos
        this.shiftCurSoloists();
    }

    // protected onCultureButtonPressed(pTopic: string, pData: Button): void {
    //     // Application.instance.audio.fadeTo(Constants.MUSIC_LAYER_TOP, HLF.AudioCategory.BGM, 1, 500);
    // }

    protected onPause(pTopic: string): void {
       this._topTrack.pause();
       this._baseTrack.pause();
    }

    protected onResume(pTopic: string): void {
        if (this._topTrack.getTimePos() > 0) {
            this._topTrack.play();
        }
        if (this._baseTrack.getTimePos() > 0) {
            this._baseTrack.play();
        }
        this.update(0);
        // this.shiftCurSoloists();
    }
}
